<template>
	<Modal :id="`create-${id}-directory-modal`">
		<template #title>{{ trans('admin.images.folder') }}</template>
		<LabeledObject full>
			<template #label>{{ trans('admin.images.name') }}</template>
			<input v-model="newDirectory" type="text">
		</LabeledObject>
		<template #footer>
			<PrimaryButton :color="Color.Green" @click.stop="mkdir">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
	</Modal>
	<Modal :id="`upload-${id}-file-modal`">
		<template #title>{{ trans('admin.images.upload') }}</template>
		<LabeledObject full>
			<template #label>{{ trans('general.select_file') }}</template>
			<input ref="picker_image" type="file" class="border-gray-200 dark:border-gray-700" accept="image/*">
		</LabeledObject>
		<template #footer>
			<PrimaryButton :color="Color.Green" @click.stop="uploadPickerImage">
				<i class="fa-solid fa-upload mr-2"/>
				{{ trans('general.upload_file') }}
			</PrimaryButton>
		</template>
	</Modal>
	<div class="fixed top-0 left-0 w-screen h-dvh flex flex-col z-[1300] bg-white dark:bg-gray-950 divide-y divide-gray-200 dark:divide-gray-800 overflow-y-auto" @click.stop="selectFile('')">
		<div class="px-3 py-2 flex items-center gap-3">
			<CloseButton @close="emit('close')"/>
			<Heading>{{ trans('admin.images.pick') }}</Heading>
		</div>
		<div class="flex items-center gap-3">
			<div class="grow p-2 flex items-end gap-3">
				<SoftButton :color="Color.Green" @click.stop="showModal(`create-${id}-directory-modal`)">
					<i class="fa-solid fa-folder-plus"/>
				</SoftButton>
				<SoftButton :color="Color.Green" @click.stop="showModal(`upload-${id}-file-modal`)">
					<i class="fa-solid fa-file-circle-plus"/>
				</SoftButton>
				<SoftButton v-if="selectedFile !== ''" :color="Color.Red" @click.stop="rm">
					<i class="fa-solid fa-file-circle-xmark"/>
				</SoftButton>
			</div>
		</div>
		<div class="grow grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-gray-200 dark:divide-gray-800 overflow-y-auto">
			<div class="lg:col-span-3 p-2 flex flex-col gap-1 overflow-y-auto">
				<button type="button" :class="`px-2 py-1 flex items-center gap-3 rounded-lg ${selectedDirectory === '/' ? 'bg-gray-100 dark:bg-gray-800' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`" @click.stop="cd('/')">
					<i class="fa-solid fa-folder text-red-700"/>
					<span class="grow text-left text-sm truncate">AArEII</span>
				</button>
				<div v-if="directories.length > 0" class="pl-3 flex flex-col gap-1">
					<template v-for="directory in directories" :key="`file-manager-directory-${selectedDirectory}-${directory.path}`">
						<FileManagerDirectory v-if="!systemDirectories.includes(directory.path)" :directory="directory" :selected-directory="selectedDirectory" @selected="cd"/>
					</template>
				</div>
			</div>
			<div class="lg:col-span-9 p-2 flex flex-col gap-2 overflow-y-auto">
				<div class="px-2 py-1 w-full lg:w-fit flex items-center gap-3 lg:place-self-start rounded-lg bg-gray-50 dark:bg-gray-900 text-sm shrink-0 overflow-x-auto whitespace-nowrap" aria-label="breadcrumb">
					<p class="text-gray-400">AArEII</p>
					<template v-for="directory in breadcrumbs">
						<i class="fa-solid fa-chevron-right text-gray-400"/>
						<p class="text-gray-400">{{ directory }}</p>
					</template>
					<Badge active>{{ transChoice('general.file_manager.files_count', files.length) }}</Badge>
					<SoftButton v-if="![...systemDirectories, '', '/'].includes(selectedDirectory)" :color="Color.Red" @click.stop="rmdir">
						<i class="fa-solid fa-trash"/>
					</SoftButton>
				</div>
				<Loading v-if="loading"/>
				<div v-else-if="files.length > 0" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 overflow-y-auto">
					<template v-for="file in files" :key="`file-picker-file-${file}`">
						<div v-if="!systemFiles.includes(file)" :class="`rounded-lg cursor-pointer ${selectedFile === file ? 'bg-gray-100 dark:bg-gray-800' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`" @click.stop="selectFile(file)" @dblclick.stop="pick(file)">
							<div class="flex flex-col gap-3 p-2 items-center">
								<div class="grow relative">
									<img class="border border-gray-200 dark:border-gray-700 rounded max-h-96 max-w-full" :src="storage(file)" :alt="file">
								</div>
								<p class="w-full text-sm truncate">{{ getFileName(file) }}</p>
							</div>
						</div>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import Loading from '@/Components/UI/Loading.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import {trans, transChoice} from 'laravel-vue-i18n'
import Heading from '@/Components/UI/Typography/Heading.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import CloseButton from '@/Components/UI/Buttons/CloseButton.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import FileManagerDirectory from '@/Components/Files/FileManagerDirectory.vue'
import {showInfo, showModal, showSuccess, showValidationErrors, showWarning, storage} from '@/Helpers/general'
import {defineEmits, defineProps, onMounted, ref, type Ref, type ShallowRef, useTemplateRef,} from 'vue'
import {type FileManagerDirectory as FileManagerDirectoryType} from '@/Types/System/FileManager/FileManagerDirectory'
import {Color} from '@/Types/Enums/Color'

const props = defineProps<{
	id?:string,
	selectedFile?:string,
}>()

const emit = defineEmits([
	'close',
	'picked',
	'update:selectedFile',
])

const id:Ref<string> = ref(props.id ?? 'file-manager')
const parent:Ref<string> = ref('')
const loading:Ref<boolean> = ref(false)
const files:Ref<Array<string>> = ref([])
const newDirectory:Ref<string> = ref('')
const selectedFile:Ref<string> = ref(props.selectedFile ?? '')
const selectedDirectory:Ref<string> = ref('/')
const breadcrumbs:Ref<Array<string>> = ref([])
const directories:Ref<Array<FileManagerDirectoryType>> = ref([])
const imagePicker:Readonly<ShallowRef<HTMLInputElement|null>> = useTemplateRef('picker_image')
const systemFiles:Ref<Array<string>> = ref([
	'background.svg',
	'background-dark.svg',
	'no_img.png',
	'.gitignore',
	'.DS_Store',
])
const systemDirectories:Ref<Array<string>> = ref([
	'flags',
	'home',
	'logos',
	'certificates',
	'reports',
	'mailing',
	'events',
	'rotations',
])

function selectFile(path:string):void{
	selectedFile.value = path
}

function selectDirectory(path:string):void{
	selectedDirectory.value = path
}

function pick(path:string):void{
	emit('update:selectedFile', path)
	emit('picked')
	emit('close')
}

async function index():Promise<void>{
	loading.value = true
	return axios.post(route('system.files.index', 'index')).then(({data}) => {
		directories.value = data
	}).catch(showValidationErrors).finally(() => {
		loading.value = false
	})
}

function cd(directory:string):void{
	if(!systemDirectories.value.includes(directory) && !loading.value){
		loading.value = true
		selectFile('')
		selectDirectory(directory)
		axios.post(route('system.files.index', 'cd'), {
			directory: directory === '' ? '/' : directory,
		}).then(({data}) => {
			files.value = data.manager.files
			breadcrumbs.value = data.breadcrumbs
			parent.value = data.parent
		}).catch(showValidationErrors).finally(() => {
			loading.value = false
		})
	}
}

function rm():void{
	if(selectedFile.value.trim().length > 0){
		deleteFileOrDirectory(selectedFile.value)
	}
}

function deleteFileOrDirectory(file:string):void{
	if(!systemFiles.value.includes(file) && !loading.value && confirm(trans('admin.images.confirm_delete'))){
		loading.value = true
		selectDirectory('')
		axios.post(route('system.files.index', 'rm'), {
			directory: breadcrumbs.value.join('/'),
			file: file,
		}).then(({data}) => {
			files.value = data.manager.files
			breadcrumbs.value = data.breadcrumbs
			parent.value = data.parent
		}).catch(showValidationErrors).finally(() => {
			loading.value = false
		})
	}
}

function rmdir():void{
	deleteFileOrDirectory(selectedDirectory.value)
}

function uploadPickerImage():void{
	if(imagePicker.value && imagePicker.value.files && imagePicker.value.files.length > 0 && !loading.value){
		loading.value = true
		let data = new FormData
		data.append('directory', breadcrumbs.value.join('/'))
		data.append('file', imagePicker.value.files[0])
		showInfo(trans('general.uploading_file'))
		axios.post(route('system.files.index', 'upload'), data, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		}).then(({data}) => {
			files.value = data.manager.files
			breadcrumbs.value = data.breadcrumbs
			parent.value = data.parent
			selectedFile.value = data.file
			showSuccess(trans('general.inserted'))
		}).catch(showValidationErrors).finally(() => {
			loading.value = false
		})
	} else {
		showWarning(trans('admin.images.select'))
	}
}

function mkdir():void{
	if(!loading.value){
		loading.value = true
		axios.post(route('system.files.index', 'mkdir'), {
			directory: breadcrumbs.value.join('/'),
			name: newDirectory.value,
		}).then(({data}) => {
			directories.value = data.manager.directories
			files.value = data.manager.files
			breadcrumbs.value = data.breadcrumbs
			parent.value = data.parent
			newDirectory.value = ''
		}).catch(showValidationErrors).finally(() => {
			loading.value = false
		})
	}
}

function getFileName(file:string):string|undefined{
	return file.split('/').pop()
}

onMounted(() => {
	index().then(() => {
		cd('/')
	})
})
</script>
